import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Delete, Science } from '@mui/icons-material';
import {
  getDefaultExtractionConfig,
  isExtractionConfigValid,
  normalizeExtractionConfig,
} from '../../utils/extractionTaxonomy';
import ExtractionConfigFormSection from '../ExtractionConfigFormSection';

/**
 * Re-run requirement extraction for a session with the same content.
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {object|null} props.session - Session to regenerate.
 * @param {boolean} props.extracting - Dispatch in flight.
 * @param {string} [props.extractionError] - Last extraction error.
 * @param {Function} props.onCancel - Close without running.
 * @param {(opts: {regenerateMode: 'delete'|'add', extractionConfig: object, targetedExtraction: string}) => void} props.onConfirm - Run.
 * @returns {JSX.Element}
 */
export function RegenerateRequirementsDialog({ open, session, extracting, extractionError, onCancel, onConfirm }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [mode, setMode] = useState('delete');
  const [config, setConfig] = useState(getDefaultExtractionConfig);
  const [focus, setFocus] = useState('');

  // Start from the session's own extraction settings each time it opens.
  useEffect(() => {
    if (open && session) {
      setMode('delete');
      setConfig(normalizeExtractionConfig(session.extractionConfig, session.extractionType));
      setFocus('');
    }
  }, [open, session]);

  return (
    <Dialog open={open} onClose={() => !extracting && onCancel()} maxWidth="md" fullWidth fullScreen={isPhone} disableRestoreFocus>
      <DialogTitle>Regenerate requirements</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {session?.name ? `"${session.name}"` : 'This session'} is processed again from its original
          {session?.file ? ` file (${session.file.name})` : ' content'}.
        </Typography>

        {extractionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {extractionError}
          </Alert>
        )}

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend" sx={{ fontSize: '0.8125rem', mb: 0.5 }}>Existing requirements</FormLabel>
          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel value="delete" control={<Radio size="small" />} label="Replace them with a fresh extraction" />
            <FormControlLabel value="add" control={<Radio size="small" />} label="Keep them and add new ones" />
          </RadioGroup>
        </FormControl>

        <ExtractionConfigFormSection value={config} onChange={setConfig} />

        <TextField
          label="Focus (optional)"
          fullWidth
          multiline
          minRows={2}
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          placeholder="Specify what to focus on during extraction"
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={extracting}>Cancel</Button>
        <Button
          onClick={() => onConfirm({ regenerateMode: mode, extractionConfig: config, targetedExtraction: focus })}
          variant="contained"
          color={mode === 'delete' ? 'error' : 'primary'}
          disabled={extracting || !isExtractionConfigValid(config)}
          startIcon={extracting ? <CircularProgress size={16} color="inherit" /> : mode === 'delete' ? <Delete /> : <Add />}
        >
          {extracting ? 'Processing...' : mode === 'delete' ? 'Replace and extract' : 'Add and extract'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Run a first extraction for a session (typically an imported meeting).
 *
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {object|null} props.session - Session to extract from.
 * @param {boolean} props.extracting - Dispatch in flight.
 * @param {string} [props.extractionError] - Last extraction error.
 * @param {Function} props.onClose - Close without running.
 * @param {(opts: {extractionConfig: object, targetedExtraction: string}) => void} props.onConfirm - Run.
 * @returns {JSX.Element}
 */
export function ExtractRequirementsDialog({ open, session, extracting, extractionError, onClose, onConfirm }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [config, setConfig] = useState(getDefaultExtractionConfig);
  const [focus, setFocus] = useState('');

  useEffect(() => {
    if (open) {
      setConfig(getDefaultExtractionConfig());
      setFocus('');
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={() => !extracting && onClose()} maxWidth="sm" fullWidth fullScreen={isPhone} disableRestoreFocus>
      <DialogTitle>Extract requirements</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Inky reads {session?.name ? `"${session.name}"` : 'this session'} and drafts requirements in the background.
            You can follow progress in the jobs panel and review the results on the Requirements step.
          </Typography>
        </Box>

        {extractionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {extractionError}
          </Alert>
        )}

        <ExtractionConfigFormSection value={config} onChange={setConfig} />

        <TextField
          label="Focus (optional)"
          fullWidth
          multiline
          minRows={2}
          value={focus}
          onChange={(e) => setFocus(e.target.value)}
          placeholder="Specify what to focus on. Leave empty for a general extraction."
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={extracting}>Cancel</Button>
        <Button
          onClick={() => onConfirm({ extractionConfig: config, targetedExtraction: focus })}
          variant="contained"
          disabled={extracting || !isExtractionConfigValid(config)}
          startIcon={extracting ? <CircularProgress size={16} color="inherit" /> : <Science />}
        >
          {extracting ? 'Extracting...' : 'Extract requirements'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
